import { useCallback, useEffect } from "react"

import { CheckOutlined, MinusOutlined } from "@ant-design/icons"
import { Drawer, Flex, Skeleton, Tag, Timeline, Typography } from "antd"
import cls from "classnames"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import Private from "../../../../../Components/Private"
import Icon from "../../../../../Icon"
import { dateFormat } from "../../../../../Utils/utils"
import { useReceiptBusinessId } from "../../../hooks/useReceiptBusinessId"
import { CASE_NETWORK_META, CASE_REASON, EVIDENCE_LABEL } from "../../constants/case-details"
import { OUTCOME_META } from "../../constants/outcomes"
import { CE_PROGRAM_META } from "../../constants/programs"
import { selectCaseDetails } from "../../redux/compelling-evidence.slice"
import { getCompellingEvidenceCase } from "../../redux/compelling-evidence.thunks"
import {
  CASE_DRAWER_PARAM,
  formatMoney,
  pastOrderMatchText,
  pastOrdersEmptyText,
  timelineColor,
  timelineSubtitle,
  timelineTitle,
  verdictText
} from "./caseTexts"

import styles from "./styles.module.less"

const { Text, Title } = Typography

const Section = ({ title, note, children }) => (
  <div>
    <Text type="secondary" className={styles.sectionTitle}>
      {title}
    </Text>
    {note && (
      <Text type="secondary" className={styles.note}>
        {note}
      </Text>
    )}
    {children}
  </div>
)

const CaseDetails = ({ detail }) => {
  const { t } = useTranslation()
  const outcome = OUTCOME_META[detail.outcome]
  const network = CASE_NETWORK_META[detail.program]
  const pastOrders = detail.past_orders ?? []

  // Visa's own threshold; Mastercard picks its past orders itself and states no minimum.
  const needsTwoMatches =
    detail.program === "ce" &&
    detail.reason === CASE_REASON.NO_MATCH &&
    pastOrders.length > 0 &&
    pastOrders.length < 2

  return (
    <Flex vertical gap={24}>
      <div>
        <Flex align="center" gap={10} className={styles.amountRow}>
          <Title level={2} className={styles.amount}>
            <Private>{formatMoney(detail.amount)}</Private>
          </Title>
          {outcome && <Tag color={outcome.color}>{t(outcome.label)}</Tag>}
        </Flex>
        <Flex vertical gap={4} className={styles.verdict}>
          <Text>{verdictText(detail, t)}</Text>
          <Text type="secondary">
            {t("Fraud dispute · reason code _code_", { code: detail.reason_code })} ·{" "}
            <Private>••••{detail.card}</Private>
          </Text>
        </Flex>
      </div>

      <Section title={t("Evidence sent")}>
        <div className={styles.evidence}>
          {(detail.evidence ?? []).map(({ field, captured }) => (
            <Flex key={field} gap={8} align="flex-start">
              <span className={cls(styles.evidenceMark, { [styles.captured]: captured })}>
                {captured ? <CheckOutlined /> : <MinusOutlined />}
              </span>
              <span>
                <Text>{t(EVIDENCE_LABEL[field])}</Text>
                {!captured && (
                  <Text type="secondary" className={styles.small}>
                    {t("Not captured")}
                  </Text>
                )}
              </span>
            </Flex>
          ))}
        </div>
      </Section>

      <Section
        title={t(network?.pastOrdersTitle ?? "")}
        note={network?.pastOrdersNote && t(network.pastOrdersNote)}
      >
        {pastOrders.length ? (
          pastOrders.map((order) => (
            <div key={order.date} className={styles.pastOrder}>
              <Flex justify="space-between">
                <Text>{dayjs(order.date).format(dateFormat)}</Text>
                <Text>
                  <Private>{formatMoney(order.amount)}</Private>
                </Text>
              </Flex>
              <Text type="secondary" className={styles.matchText}>
                {pastOrderMatchText(order, detail, t)}
              </Text>
            </div>
          ))
        ) : (
          <Text type="secondary">{pastOrdersEmptyText(detail, t)}</Text>
        )}
        {needsTwoMatches && (
          <Text type="secondary" className={styles.twoMatchesNote}>
            {t("Visa needs 2 matching orders.")}
          </Text>
        )}
      </Section>

      <Section title={t("Timeline")}>
        <Timeline
          className={styles.timeline}
          items={(detail.timeline ?? []).map((item) => ({
            key: `${item.event}-${item.at}`,
            color: timelineColor(item),
            children: (
              <>
                <Text strong>{timelineTitle(item, detail, t)}</Text>
                <Text type="secondary" className={styles.timelineSubtitle}>
                  {timelineSubtitle(item, t)}
                </Text>
              </>
            )
          }))}
        />
      </Section>
    </Flex>
  )
}

export const CaseDrawer = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [search, setSearch] = useSearchParams()
  const businessId = useReceiptBusinessId()
  const isMobile = useSelector((s) => s.nav.isMobile)
  const { data, fetching } = useSelector(selectCaseDetails)

  const caseId = search.get(CASE_DRAWER_PARAM)
  const program = data && CE_PROGRAM_META[data.program]

  useEffect(() => {
    if (!caseId) return
    const request = dispatch(getCompellingEvidenceCase({ id: caseId, businessId }))
    return () => request.abort()
  }, [dispatch, caseId, businessId])

  const handleClose = useCallback(() => {
    const next = new URLSearchParams(search)
    next.delete(CASE_DRAWER_PARAM)
    setSearch(next, { replace: true })
  }, [search, setSearch])

  return (
    <Drawer
      open={!!caseId}
      onClose={handleClose}
      placement="right"
      width={isMobile ? "100%" : 440}
      title={
        program && (
          <Flex align="center" gap={8} className={styles.header}>
            {program.iconType && <Icon type={program.iconType} style={{ fontSize: 20 }} />}
            {t(program.name)}
          </Flex>
        )
      }
      data-testid="deflect-ce-case-drawer"
    >
      {fetching || !data ? (
        <Skeleton active paragraph={{ rows: 12 }} />
      ) : (
        <CaseDetails detail={data} />
      )}
    </Drawer>
  )
}
